import React from 'react';

import { button, icon, text } from '@harmony/enablers/react';

import { getHarmonyScope } from '../../../Services/Services.Secondary';
import LocalizationService from '../../../Services/Services.Secondary/Localization/LocalizationService';
import { displayDate, getLanguageFromBrowser, isEmptyOrSpaces, truncateContent } from '../../../Utilities/Helpers';
import { Incident } from './Models/ServiceHealthTables';

const Button = getHarmonyScope().forReact(button);
const Icon = getHarmonyScope().forReact(icon);
const Text = getHarmonyScope().forReact(text);

const resx = LocalizationService.getInstance();
const truncatedThreshold = 180;

let Summary_text = resx.getLocalizedResource('Summary_text');
let RootCause_Text = resx.getLocalizedResource('RootCause_Text');
let Mitigation_Text = resx.getLocalizedResource('Mitigation_Text');
const ViewMoreButtonServiceAnnouncementMessage_text = resx.getLocalizedResource(
  'ViewMoreButtonServiceAnnouncementMessage_text'
);
const ShowLessButtonServiceAnnouncementMessage_text = resx.getLocalizedResource(
  'ShowLessButtonServiceAnnouncementMessage_text'
);

type HealthHistoryTableRowProps = {
  incident: Incident;
  id: number;
};

type HealthHistoryTableRowState = {
  showFullDescription: boolean;
};

export default class HealthHistoryTableRow extends React.Component<
  HealthHistoryTableRowProps,
  HealthHistoryTableRowState
> {
  constructor(props: HealthHistoryTableRowProps) {
    super(props);
    this.state = {
      showFullDescription: false,
    };
  }

  private changeFullDescription = () => {
    this.setState({ showFullDescription: !this.state.showFullDescription });
  };

  private getFormattedText = (
    heading: string,
    message: string | undefined,
    truncated: boolean,
    spacingTop: boolean
  ) => {
    if (message && !isEmptyOrSpaces(message)) {
      const truncatedMessage = truncated ? truncateContent(message, truncatedThreshold)[0] : message;
      return (
        <div className={spacingTop ? 'spacer-xxs-top' : ''}>
          <Text>
            <b>
              {heading}
              {': '}
            </b>
            {truncatedMessage}
          </Text>
        </div>
      );
    }
  };

  private getDescription = () => {
    const { incident } = this.props;
    const { description, rootCause, resolution } = incident;
    const { showFullDescription } = this.state;
    const expandButton = this.getExpandButton();

    const descriptionText = this.getFormattedText(Summary_text, description, !showFullDescription, false);
    const rootCauseText = this.getFormattedText(RootCause_Text, rootCause, false, true);
    const mitigationText = this.getFormattedText(Mitigation_Text, resolution, false, true);

    return (
      <span slot={`description-${this.props.id}`}>
        <div className="description-container">
          <div>
            {descriptionText}
            {showFullDescription && rootCauseText}
            {showFullDescription && mitigationText}
          </div>
          {expandButton}
        </div>
      </span>
    );
  };

  private getExpandButton = () => {
    const { incident } = this.props;
    const { resolution, rootCause, description } = incident;
    const { showFullDescription } = this.state;
    const hasDescription = description.length > truncatedThreshold;
    const hasContent = !isEmptyOrSpaces(resolution ?? '') || !isEmptyOrSpaces(rootCause ?? '') || hasDescription;

    if (!hasContent) {
      return null;
    }

    const buttonText = showFullDescription
      ? ShowLessButtonServiceAnnouncementMessage_text
      : ViewMoreButtonServiceAnnouncementMessage_text;
    const icon = showFullDescription ? 'chevronup' : 'chevrondown';

    return (
      <div className="description-expand-button">
        <Button appearance="stealth" onClick={this.changeFullDescription} aria-label={buttonText}>
          <Icon slot="start" name={icon} />
        </Button>
      </div>
    );
  };

  public render() {
    const incident = this.props.incident;
    const locale = getLanguageFromBrowser();
    if (incident && incident.date && incident.description) {
      return (
        <>
          <span slot={`date-${this.props.id}`}>
            <Text>{displayDate(incident.date, locale)}</Text>
          </span>
          <span slot={`workspace-${this.props.id}`}>
            <Text>{incident.workspace}</Text>
          </span>
          <span slot={`service-${this.props.id}`}>
            <Text>{incident.serviceName}</Text>
          </span>
          {this.getDescription()}
        </>
      );
    }

    return null;
  }
}
